<script setup>
import { useI18n } from 'vue-i18n';

defineProps({
  note: { type: Object, required: true },
  canDelete: { type: Boolean, default: false },
  canEdit: { type: Boolean, default: false },
});

const emit = defineEmits(['delete', 'edit']);
const { t } = useI18n();

const formatDate = value => {
  if (!value) return t('POST_SALE.COMMON.NO_DATE');
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const firstName = name =>
  name?.trim()?.split(/\s+/)[0] || t('POST_SALE.NOTES.UNKNOWN_AUTHOR');
</script>

<template>
  <article
    data-testid="post-sale-note-item"
    class="group flex flex-col gap-4 border-b border-lais-border-soft bg-white px-6 py-6 transition hover:bg-[#E0E7EB] sm:flex-row sm:items-center sm:justify-between"
  >
    <div class="flex min-w-0 flex-1 gap-5 sm:px-5">
      <div class="flex shrink-0 flex-col items-center text-center">
        <span
          class="grid size-[30px] place-items-center rounded-full bg-lais-primary-50 text-xs font-semibold text-lais-primary"
          aria-hidden="true"
        >
          {{ firstName(note.author?.name).slice(0, 1).toUpperCase() }}
        </span>
        <span
          data-testid="post-sale-note-author"
          class="mt-1 text-xs text-lais-text-body"
        >
          {{ firstName(note.author?.name) }}
        </span>
      </div>
      <div class="min-w-0 flex-1">
        <h3 class="text-base font-semibold text-lais-text">
          {{ note.title }}
        </h3>
        <p class="mt-1 whitespace-pre-wrap text-sm text-lais-text-body">
          {{ note.description }}
        </p>
      </div>
    </div>
    <div class="flex shrink-0 flex-wrap items-center justify-end gap-3">
      <div
        data-testid="post-sale-note-info"
        class="flex items-center gap-3 sm:group-hover:hidden"
      >
        <span
          v-if="note.important"
          class="rounded-full bg-[#FFF7E5] px-2 py-1 text-xs font-semibold text-[#D69500]"
        >
          {{ t('POST_SALE.NOTES.IMPORTANT') }}
        </span>
        <time class="text-xs text-lais-text-muted">
          {{ formatDate(note.created_at) }}
        </time>
      </div>
      <div
        data-testid="post-sale-note-actions"
        class="flex gap-2 sm:opacity-0 sm:transition sm:group-hover:opacity-100"
      >
        <button
          v-if="canEdit"
          type="button"
          class="rounded-lais px-2 py-1 text-xs font-semibold text-lais-text-body hover:bg-white"
          @click="emit('edit', note)"
        >
          <span
            class="i-lucide-pencil mr-1 inline-block size-4"
            aria-hidden="true"
          />
          {{ t('POST_SALE.NOTES.ACTIONS.EDIT') }}
        </button>
        <button
          v-if="canDelete"
          type="button"
          class="rounded-lais px-2 py-1 text-xs font-semibold text-lais-text-body hover:bg-white"
          @click="emit('delete', note)"
        >
          <span
            class="i-lucide-x mr-1 inline-block size-4"
            aria-hidden="true"
          />
          {{ t('POST_SALE.NOTES.ACTIONS.DELETE') }}
        </button>
      </div>
    </div>
  </article>
</template>
